import { useCallback, useEffect, useState } from 'react'
import { Copy, ExternalLink, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import {
  partnerInterestsService,
  type PartnerInterest,
  type PartnerLeadAISuggestion,
  type PartnerInterestStatus,
  type PartnerInterestUpdate,
} from '@/services/partnerInterests.service'
import {
  PARTNER_INTEREST_STATUSES,
  PARTNER_INTEREST_STATUS_META,
} from './partnerInterestStatus'

interface Props {
  interestId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: () => void
}

interface FormState {
  status: PartnerInterestStatus
  qualificationNotes: string
  nextFollowUpAt: string
  lostReason: string
}

const EMPTY_FORM: FormState = {
  status: 'novo',
  qualificationNotes: '',
  nextFollowUpAt: '',
  lostReason: '',
}

function toLocalDateTime(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function toIsoDateTime(value: string) {
  return value ? new Date(value).toISOString() : null
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-'
}

function formFromInterest(interest: PartnerInterest): FormState {
  return {
    status: interest.status,
    qualificationNotes: interest.qualification_notes || '',
    nextFollowUpAt: toLocalDateTime(interest.next_follow_up_at),
    lostReason: interest.lost_reason || '',
  }
}

function buildPayload(form: FormState): PartnerInterestUpdate {
  return {
    status: form.status,
    qualification_notes: form.qualificationNotes.trim() || null,
    next_follow_up_at: toIsoDateTime(form.nextFollowUpAt),
    lost_reason: form.status === 'perdido' ? form.lostReason.trim() || null : null,
  }
}

export function PartnerInterestDetailDrawer({
  interestId,
  open,
  onOpenChange,
  onUpdated,
}: Props) {
  const [interest, setInterest] = useState<PartnerInterest | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [initialPayload, setInitialPayload] = useState<PartnerInterestUpdate | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [aiSuggestion, setAiSuggestion] = useState<PartnerLeadAISuggestion | null>(null)
  const [aiDraftMessage, setAiDraftMessage] = useState('')

  const applyInterest = useCallback((next: PartnerInterest) => {
    const nextForm = formFromInterest(next)
    setInterest(next)
    setForm(nextForm)
    setInitialPayload(buildPayload(nextForm))
    setInviteLink(next.invite?.link ?? null)
  }, [])

  useEffect(() => {
    if (!open || interestId === null) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setInviteLink(null)
    setAiSuggestion(null)
    setAiDraftMessage('')

    void partnerInterestsService
      .get(interestId)
      .then((next) => {
        if (!cancelled) applyInterest(next)
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Falha ao carregar o lead.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applyInterest, interestId, open])

  const payload = buildPayload(form)
  const hasChanges =
    initialPayload !== null && JSON.stringify(payload) !== JSON.stringify(initialPayload)
  const invalidLostReason = form.status === 'perdido' && !form.lostReason.trim()

  async function handleSave() {
    if (interestId === null || invalidLostReason) return

    setSaving(true)
    try {
      const updated = await partnerInterestsService.update(interestId, payload)
      applyInterest(updated)
      toast.success('Lead atualizado.')
      onUpdated()
    } catch (cause: unknown) {
      toast.error(cause instanceof Error ? cause.message : 'Falha ao atualizar o lead.')
    } finally {
      setSaving(false)
    }
  }

  async function handleGenerateInvite() {
    if (interestId === null) return

    setGenerating(true)
    try {
      const result = await partnerInterestsService.generateInvite(interestId)
      setInviteLink(result.link)
      applyInterest(await partnerInterestsService.get(interestId))
      toast.success('Convite gerado.')
      onUpdated()
    } catch (cause: unknown) {
      toast.error(cause instanceof Error ? cause.message : 'Falha ao gerar convite.')
    } finally {
      setGenerating(false)
    }
  }

  async function handleSuggestNextAction() {
    if (interestId === null || !interest) return

    setSuggesting(true)
    try {
      const result = await partnerInterestsService.suggestNextAction(interestId, {
        action: 'suggest_next_action',
        objective: 'Sugerir proxima acao para qualificacao do lead SaaS.',
        context: {
          status: interest.status,
          qualification_notes: interest.qualification_notes,
          next_follow_up_at: interest.next_follow_up_at,
        },
      })
      setAiSuggestion(result)
      setAiDraftMessage(result.draft_message)
      toast.success('Sugestão gerada.')
    } catch (cause: unknown) {
      toast.error(cause instanceof Error ? cause.message : 'Falha ao gerar sugestão.')
    } finally {
      setSuggesting(false)
    }
  }

  async function handleCopyInvite() {
    if (!inviteLink) return

    try {
      await navigator.clipboard.writeText(inviteLink)
      toast.success('Link copiado.')
    } catch {
      toast.error('Não foi possível copiar o link.')
    }
  }

  async function handleCopyAiDraft() {
    if (!aiDraftMessage.trim()) return

    try {
      await navigator.clipboard.writeText(aiDraftMessage)
      toast.success('Rascunho copiado.')
    } catch {
      toast.error('Não foi possível copiar o rascunho.')
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full max-w-[520px]" aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>{interest?.name || 'Lead SaaS'}</SheetTitle>
          <SheetDescription>{interest?.email || 'Carregando detalhes...'}</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando detalhes...
          </div>
        ) : error ? (
          <p className="py-8 text-sm text-destructive">{error}</p>
        ) : interest ? (
          <div className="flex flex-col gap-6">
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Telefone</dt>
                <dd>{interest.phone || '-'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Empresa</dt>
                <dd>{interest.company_name || '-'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Documento</dt>
                <dd>{interest.doc || '-'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Origem</dt>
                <dd>{interest.source || '-'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Recebido</dt>
                <dd>{formatDate(interest.created_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Último contato</dt>
                <dd>{formatDate(interest.last_contacted_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Status alterado</dt>
                <dd>{formatDate(interest.status_changed_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Atualizado</dt>
                <dd>{formatDate(interest.updated_at)}</dd>
              </div>
            </dl>

            {interest.message && (
              <div>
                <p className="text-sm font-medium">Mensagem</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {interest.message}
                </p>
              </div>
            )}

            {aiSuggestion && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="font-medium">Sugestão da IA</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleCopyAiDraft()}
                  >
                    <Copy className="h-4 w-4" />
                    Copiar rascunho
                  </Button>
                </div>
                {aiSuggestion.summary && (
                  <p className="text-muted-foreground">{aiSuggestion.summary}</p>
                )}
                {aiSuggestion.questions.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                    {aiSuggestion.questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                )}
                {aiSuggestion.next_action && (
                  <p className="mt-2 font-medium">{aiSuggestion.next_action}</p>
                )}
                <Textarea
                  id="ai-draft-message"
                  className="mt-3"
                  value={aiDraftMessage}
                  onChange={(event) => setAiDraftMessage(event.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="partner-status">Status</Label>
              <select
                id="partner-status"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.status}
                onChange={(event) => {
                  const status = event.target.value as PartnerInterestStatus
                  setForm((current) => ({ ...current, status }))
                }}
              >
                {PARTNER_INTEREST_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {PARTNER_INTEREST_STATUS_META[status].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qualification-notes">Notas de qualificação</Label>
              <Textarea
                id="qualification-notes"
                value={form.qualificationNotes}
                onChange={(event) => {
                  const qualificationNotes = event.target.value
                  setForm((current) => ({ ...current, qualificationNotes }))
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="next-follow-up">Próximo follow-up</Label>
              <Input
                id="next-follow-up"
                type="datetime-local"
                value={form.nextFollowUpAt}
                onChange={(event) => {
                  const nextFollowUpAt = event.target.value
                  setForm((current) => ({ ...current, nextFollowUpAt }))
                }}
              />
            </div>

            {form.status === 'perdido' && (
              <div className="space-y-2">
                <Label htmlFor="lost-reason">Motivo da perda</Label>
                <Input
                  id="lost-reason"
                  maxLength={120}
                  required
                  value={form.lostReason}
                  onChange={(event) => {
                    const lostReason = event.target.value
                    setForm((current) => ({ ...current, lostReason }))
                  }}
                />
              </div>
            )}

            {inviteLink && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <a
                  href={inviteLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 break-all text-primary underline"
                >
                  Abrir convite
                  <ExternalLink className="h-4 w-4" />
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => void handleCopyInvite()}
                >
                  <Copy className="h-4 w-4" />
                  Copiar link
                </Button>
              </div>
            )}
          </div>
        ) : null}

        <SheetFooter>
          {interest && (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleSuggestNextAction()}
              disabled={saving || generating || suggesting}
            >
              {suggesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Sugerir próxima ação
            </Button>
          )}
          {interest && !interest.invite_id && (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleGenerateInvite()}
              disabled={saving || generating || suggesting}
            >
              {generating && <Loader2 className="h-4 w-4 animate-spin" />}
              Gerar convite
            </Button>
          )}
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || generating || suggesting || !hasChanges || invalidLostReason}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Salvar alterações
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
